import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch } from 'react-hook-form';
import cx from 'clsx';

import { AntdSelect } from '@/components/shared/AntdSelect';
import { Input, Button, Tips, Loading, TokenAvatar } from '@/components';
import { AccountStatus } from '@/consts';
import { useCurrentAddressInfo, useCurrentAddressAccount, useL2AccountStatus } from '@/hooks';
import { numFormat, getWithdrawTx, formatNum } from '@/utils';

import { useWithdrawL2, useL2WithdrawFee, useSupportedWithdrawToken } from '../../../hooks';
import { AccountModal } from '../AccountModal';
import { PasswordInput, PasswordErrorType } from '../../PasswordInput';
import style from './index.module.css';

const useWithdraw = (turnOff: () => void) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [buttonText, setButtonText] = useState(t('WithdrawModal.withdraw'));

  const { address, account: currentAccountInfo } = useCurrentAddressInfo();
  const { data: myAccountInfo, isLoading: isAccountInfoLoading } = useCurrentAddressAccount();
  const currentAccountBalance = useMemo(
    () => Math.floor((myAccountInfo?.balance || 0) * 100) / 100,
    [myAccountInfo?.balance],
  );

  const [selectedTokenId, setSelectedTokenId] = useState<string>();
  const { data: tokenList } = useSupportedWithdrawToken();
  const { mutateAsync } = useWithdrawL2();
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [passwordErrorType, setPasswordErrorType] = useState<PasswordErrorType>();

  const selectedToken = useMemo(
    () => tokenList?.find(x => `${x.chain}_${x.id}` === selectedTokenId),
    [selectedTokenId, tokenList],
  );

  useEffect(() => {
    if (tokenList?.length && !selectedTokenId) {
      setSelectedTokenId(`${tokenList[0].chain}_${tokenList[0].id}`);
    }
  }, [tokenList, selectedTokenId]);

  const { data: feeUsd = 0 } = useL2WithdrawFee(selectedToken?.chain, selectedToken?.id);

  const isBalanceLoading = useMemo(
    () => !!selectedToken && typeof selectedToken?.withdrawable_balance === 'undefined',
    [selectedToken],
  );

  const isSkipPassword = useMemo(
    () => true || (!isAccountInfoLoading && myAccountInfo?.free_password),
    [myAccountInfo?.free_password, isAccountInfoLoading],
  );

  const status = useL2AccountStatus();

  const showKeyError = useMemo(
    () => !isSkipPassword && status !== AccountStatus.NORMAL,
    [isSkipPassword, status],
  );

  const tokenAmount = useMemo(() => {
    if (selectedToken && !isBalanceLoading) {
      return selectedToken?.withdrawable_balance
        ? Math.floor(selectedToken?.withdrawable_balance * 100) / 100
        : 0;
    }
    return 0;
  }, [isBalanceLoading, selectedToken]);

  const selectedTokenSymbol = useMemo(() => selectedToken?.optimized_symbol, [selectedToken]);

  const maxAmount = useMemo(
    () => Math.min(tokenAmount, currentAccountBalance) || 0,
    [currentAccountBalance, tokenAmount],
  );
  const { control, setValue, formState } = useForm({
    mode: 'onChange',
  });

  const handleMaxClick = useCallback(() => {
    setValue('amount', maxAmount, {
      shouldValidate: true,
    });
  }, [setValue, maxAmount]);

  const amount = useWatch({
    control,
    name: 'amount',
  });

  const validateInput = useCallback(
    (amount: string) => {
      return Number(amount) >= feeUsd;
    },
    [feeUsd],
  );

  const actualRecipt = useMemo(() => {
    return !formState.isValid ? NaN : amount - feeUsd > 0 ? amount - feeUsd : NaN;
  }, [formState.isValid, amount, feeUsd]);

  const onFinishPassword = useCallback(
    async (next: () => void) => {
      setIsLoading(true);
      const secret = passwordInputRef?.current?.value;
      const nonce = myAccountInfo?.nonce || 0;

      try {
        const tx = getWithdrawTx(
          address!,
          feeUsd,
          actualRecipt,
          nonce,
          currentAccountInfo?.encryptedPrivateKey!,
          secret!,
          selectedToken!.chain,
          selectedToken!.id,
          selectedTokenSymbol,
        );
        await mutateAsync(tx).catch(e => {
          setPasswordErrorType(PasswordErrorType.VERIFY_ERROR);
        });
        turnOff();
      } catch (e) {
        console.log(e);
        setPasswordErrorType(PasswordErrorType.WRONG_PASSWORD);
        if (passwordInputRef?.current?.value) {
          passwordInputRef.current!.value = '';
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      myAccountInfo?.nonce,
      address,
      feeUsd,
      actualRecipt,
      currentAccountInfo?.encryptedPrivateKey,
      selectedToken,
      selectedTokenSymbol,
      mutateAsync,
      turnOff,
    ],
  );

  const insufficientPlatformBalance = useMemo(
    () => !isBalanceLoading && amount && Number(amount) > tokenAmount,
    [amount, isBalanceLoading, tokenAmount],
  );
  const insufficientUserBalance = useMemo(
    () => !isAccountInfoLoading && amount && Number(amount) > currentAccountBalance,
    [isAccountInfoLoading, amount, currentAccountBalance],
  );

  const steps = [
    {
      handler: (next: () => void) => {
        next();
        setButtonText(t('WithdrawModal.confirm'));
      },
      content: (
        <div className={style.withdraw}>
          <div>
            <div className={style.depositItem}>
              <div className={style.title}>{t('WithdrawModal.2phgkn')}</div>
              <div className={style.content}>
                <div className={style.selectToken}>
                  <AntdSelect
                    dropdownClassName={style.selectTokenList}
                    select={selectedTokenId}
                    width={'435px'}
                    height={'52px'}
                    placeholder={t('WithdrawModal.h4xbw6')}
                    selectOptions={(tokenList || []).map(token => ({
                      mode: `${token.chain}_${token.id}`,
                      label: (
                        <div className={style.selectOption}>
                          <TokenAvatar
                            className={style.selectOptionIcon}
                            logo={token.logo_url}
                            chain={token.chain}
                            size={24}
                            chainSize={14}
                          />
                          <div className={style.tokenSymbol}>{token.optimized_symbol}</div>
                          <div className={style.currentAmount}>
                            {t('WithdrawModal.myavfi')}{' '}
                            {formatNum(token?.withdrawable_balance, 2, { truncate: true })}
                          </div>
                        </div>
                      ),
                    }))}
                    setSelect={setSelectedTokenId}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={style.depositItem}>
            <div className={cx(style.title, style.amountTitle)}>{t('WithdrawModal.ih7pj7')}</div>
            <div className={style.tokenAmount}>
              <div
                className={cx(
                  style.currentUserAmount,
                  insufficientUserBalance && style.insufficientBalance,
                )}>
                {t('WithdrawModal.567rzk')}
                {isAccountInfoLoading ? <Loading /> : `$${currentAccountBalance}`}
              </div>
              <Controller
                name="amount"
                control={control}
                rules={{ max: maxAmount, required: true, validate: validateInput }}
                render={({ field }) => (
                  <Input
                    className={style.tokenAmountInput}
                    placeholder="0.00"
                    error={!!formState.errors.amount}
                    disabled={!address || maxAmount <= 0}
                    {...field}
                  />
                )}
              />
              <Button type="text" className={style.tokenAmountMax} onClick={handleMaxClick}>
                {t('WithdrawModal.1nic')}
              </Button>
            </div>
          </div>

          <div className={style.depositItem}>
            <div className={style.title}></div>
            <div className={style.description}>
              <div className={style.descItem}>
                <div className={style.descLabel}>
                  {t('WithdrawModal.1iee')}{' '}
                  <Tips
                    title={
                      <div className={style.tooltip}>
                        {/* <div className={style.tooltipTitle}>Withdraw Fee:</div> */}
                        <div>{t('WithdrawModal.17stwf6')}</div>
                        <div>{t('WithdrawModal.1lxf1v8')}</div>
                      </div>
                    }
                  />
                </div>
                <div className={style.descValue}>
                  {Number.isNaN(feeUsd) ? '--' : numFormat(feeUsd, 4)}
                </div>
              </div>
              <div className={style.descItem}>
                <div className={style.descLabel}>{t('WithdrawModal.1tdyu2g')}</div>
                <div className={style.descValue}>
                  {Number.isNaN(actualRecipt) ? '--' : actualRecipt.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      handler: (next: () => void) => {
        const password = passwordInputRef?.current?.value;
        if (!isSkipPassword && (!password || password.length !== 6)) {
          return;
        }

        onFinishPassword(next);
      },
      textWidth: 114,
      content: (
        <>
          <div className={style.passwordTitle}>
            {t('WithdrawModal.withdrawL1', { token: `${amount} ${selectedTokenSymbol}` })}
          </div>
          <div className={style.passwordDescription}></div>
          {isSkipPassword ? null : (
            <PasswordInput
              passwordInputRef={passwordInputRef}
              showError={!!passwordErrorType}
              errorType={passwordErrorType}
            />
          )}
        </>
      ),
    },
  ];

  return {
    steps,
    isLoading,
    successText: '',
    buttonText,
    isButtonDisabled:
      !formState.isValid ||
      !feeUsd ||
      !address ||
      Number(amount) <= Number(feeUsd.toFixed(4)) ||
      insufficientPlatformBalance ||
      insufficientUserBalance ||
      showKeyError,
  };
};

export const WithdrawModal = ({ turnOff }: { turnOff(): void }) => {
  const { t } = useTranslation();
  const { steps, isLoading, successText, buttonText, isButtonDisabled } = useWithdraw(turnOff);

  return (
    <AccountModal
      headerTitle={t('WithdrawModal.withdrawTilte')}
      turnOff={turnOff}
      steps={steps}
      hideSteps
      containerClass={style.modalContainer}
      isLoading={isLoading}
      successText={successText}
      buttonText={buttonText}
      isButtonDisabled={isButtonDisabled}
    />
  );
};
